import { BusinessLogicProviders } from 'app/components/ReactContexts';
import { FullscreenWordmarkLoader } from 'app/components/loaders';
import { InitializationErrorPage } from 'app/pages/InitializationErrorPage';
import React, { useEffect } from 'react';
import { useGetResourcesQuery } from 'services/api/resources/useGetResources';
import { selectActiveUser } from 'services/api/user-info/selectors';
import { selectAppInitStatus } from 'services/app-init/appInitSlice';
import { initApp } from 'services/app-init/initApp';
import { useAppDispatch, useAppSelector } from 'services/store';

interface Props {
  children: React.ReactNode;
}

export default function AppInitializer({ children }: Props) {
  const dispatch = useAppDispatch();

  // Once equipment fetching is migrated to RTK Query, we can consolidate
  // the loading states into a single component or hook to manage and render loaders or skeletons.
  const { isFetching: isResourcesFetching, isError: isResourcesError } =
    useGetResourcesQuery();

  const { isLoading, isError } = useAppSelector(selectAppInitStatus);
  const activeUser = useAppSelector(selectActiveUser);

  useEffect(() => {
    dispatch(initApp());
  }, [dispatch]);

  if (isLoading || isResourcesFetching) {
    return <FullscreenWordmarkLoader />;
  }

  if (isError || isResourcesError) {
    return <InitializationErrorPage />;
  }

  // In case the key changes, we guarantee that all components will be unmounted
  // and the Context API will return to its initial state.
  const providersKey = activeUser?.email ? `user-${activeUser.email}` : 'guest';

  return (
    <BusinessLogicProviders key={providersKey}>
      {children}
    </BusinessLogicProviders>
  );
}
